import { If, VLazyLoadMultiSelect, VLazyLoadSelect } from '@vision/ui/components';
import { useSelectedAccountId } from '@vision/ui/hooks';
import { ReportsDetailGraphicSchemaValues, SimpleFlowQuestion } from '@vision/ui/interfaces';
import {
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
} from '@vision/ui/services';
import { translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { ReportsDetailGraphicCompareLayout } from '../ReportsDetailGraphicCompareLayout';
import { ReportsDetailGraphicBaseProps } from '../report-detail-graphic-compare.util';

interface ReportsDetailGraphicCompareQuestionCategoryProps extends ReportsDetailGraphicBaseProps {}

export function ReportsDetailGraphicCompareQuestionCategory({
  compareItemType,
  required,
}: ReportsDetailGraphicCompareQuestionCategoryProps) {
  const { t } = useTranslation(['translation', 'page-reports']);
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();

  const isFlowSelected = formik.values.selectedCompareCriteria.selectedQuestionCategoryFlowIds.length > 0;

  const handleDefaultValuesLoaded = (data: SimpleFlowQuestion[]) => {
    const validCategoryIds = new Set(data.map((item) => item.category?.id).filter(Boolean));

    const newIds = formik.values.selectedCompareCriteria.selectedQuestionCategoryIds.filter((id) =>
      validCategoryIds.has(id),
    );

    formik.setFieldValue('selectedCompareCriteria.selectedQuestionCategoryIds', newIds);
  };

  const mapItemToSelectOption = (item: SimpleFlowQuestion) => ({
    value: item.category?.id,
    label: item.category?.name,
  });

  const getUniqueCategories = (items: SimpleFlowQuestion[]) => {
    const seen = new Set<string>();
    return items
      .filter((item) => {
        if (!item.category?.id || seen.has(item.category.id)) {
          return false;
        }
        seen.add(item.category.id);
        return true;
      })
      .map(mapItemToSelectOption);
  };

  return (
    <ReportsDetailGraphicCompareLayout title={t(`page-reports:reportCompareGroupItemType.${compareItemType}`)}>
      <VLazyLoadSelect
        id="report-compare-question-category-flow"
        data-testid="input-report-compare-question-category-flow"
        label={t('flows')}
        useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
        apiRequestParameters={{
          nodeId: accountId,
          questionCategoryNotNull: true,
          sortBy: 'name',
          sortOrder: 'asc',
        }}
        multiSelectItemMapper={(items) => {
          return items.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        }}
        searchKey="flow_name"
        value={formik.values.selectedCompareCriteria.selectedQuestionCategoryFlowIds[0]}
        onChange={(value) => formik.setFieldValue('selectedCompareCriteria.selectedQuestionCategoryFlowIds', [value])}
        onBlur={formik.handleBlur}
        error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionCategoryFlowIds')}
        withAsterisk={required}
      />

      <If value={isFlowSelected}>
        <VLazyLoadMultiSelect
          id="report-compare-flow-question-category-name"
          data-testid="input-report-compare-flow-question-category-name"
          label={t('question-category')}
          useLazyApiQueryFunction={useLazyFetchFlowQuestionsWithPaginationQuery}
          apiRequestParameters={{
            nodeId: accountId,
            flowIds: formik.values.selectedCompareCriteria.selectedQuestionCategoryFlowIds,
            categoryNotNull: true,
            sortBy: 'name',
            sortOrder: 'asc',
          }}
          multiSelectItemMapper={getUniqueCategories}
          searchKey="category_name"
          filterKey="category_ids"
          value={formik.values.selectedCompareCriteria.selectedQuestionCategoryIds}
          onBlur={formik.handleBlur}
          onChange={(value) => formik.setFieldValue('selectedCompareCriteria.selectedQuestionCategoryIds', value)}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionCategoryIds')}
          withAsterisk={required}
          resetChangeRequestParameters={true}
          onDefaultValuesLoaded={handleDefaultValuesLoaded}
        />
      </If>
    </ReportsDetailGraphicCompareLayout>
  );
}
